import { type ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router';

import { routes } from '@/app/routes';
import { Logo } from '@/components/ui/Logo';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

import { ProfileMenu } from './ProfileMenu';

import styles from './AppLayout.module.css';

interface NavItem {
    to: string;
    label: string;
    /** Shorter wording for the mobile bar, where space is tight. */
    shortLabel: string;
    icon: ReactNode;
    /** Matches nested routes too, e.g. /trainingen/squat-flow. */
    end?: boolean;
    /**
     * Extra paths that should light this item up.
     *
     * For a tab that fronts a section rather than a page: "Trainen" leads to
     * `/programmas` but is equally the answer for `/trainingen` and
     * `/oefeningen`.
     */
    match?: readonly string[];
}

const NAV_ITEMS: readonly NavItem[] = [
    {
        to: routes.dashboard,
        label: 'Vandaag',
        shortLabel: 'Vandaag',
        end: true,
        icon: <path d="M4 11.5 12 5l8 6.5V19a1 1 0 0 1-1 1h-4v-5H9v5H5a1 1 0 0 1-1-1Z" />,
    },
    /*
     * The three levels of the training hierarchy, each in the bar.
     *
     * ADR 0018 collapsed them into one "Trainen" tab, and its postscript then
     * put them behind a disclosure on that tab. The owner has now seen both and
     * asked for this: *"ik wil toch deze 3 submenus in het normale menu. Dus:
     * Vandaag Programma's Trainingen Oefeningen."* His screen, his call — and
     * the second postscript on ADR 0018 records what the ADR's own measurement
     * still protects, which is the touch bar's label widths.
     */
    {
        to: routes.programmes,
        label: "Programma's",
        shortLabel: 'Schema',
        icon: <path d="M4 6h16M4 12h16M4 18h10" />,
    },
    {
        to: routes.workouts,
        label: 'Trainingen',
        shortLabel: 'Training',
        icon: <path d="M6.5 9v6M17.5 9v6M4 10.5v3M20 10.5v3M8 12h8" />,
    },
    {
        to: routes.exercises,
        label: 'Oefeningen',
        shortLabel: 'Oefening',
        icon: <path d="M12 4v16M8 8v8M16 8v8" />,
    },
];

/**
 * Your own screens, reached through the profile rather than the bar.
 *
 * The split is the owner's: *"vanuit het menu je training oppakken, de
 * oefeningen bekijken en je dashboard zien"*, and everything that is about you
 * — progress, history, measurements, equipment — behind the profile, where you
 * go to look at what belongs to you.
 *
 * It also fixes a sizing problem honestly rather than by shrinking type: seven
 * items clipped the last label at 375px. Measured, not guessed.
 */
const PROFILE_ITEM: NavItem = {
    to: routes.settings,
    label: 'Profiel',
    shortLabel: 'Profiel',
    icon: <path d="M12 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM5 20a7 7 0 0 1 14 0" />,
};

/**
 * Frame for every authenticated screen.
 *
 * Navigation lives in a top bar on desktop and a bottom bar on touch devices —
 * two separate layouts rather than one shrunk one, because thumb reach and
 * pointer reach are genuinely different problems.
 */
export function AppLayout() {
    const { pathname } = useLocation();

    /**
     * Whether a bar item should read as current.
     *
     * Computed here rather than taken from `NavLink`'s `isActive`, because
     * `isActive` only knows the item's own `to`. That is right for a tab that
     * *is* a page and wrong for one that fronts a section: standing on
     * `/oefeningen` lit nothing up at all, so the reader was demonstrably inside
     * Trainen and the bar said they were nowhere.
     *
     * Doing it by hand also fixes the half of that bug which is invisible.
     * `NavLink` sets `aria-current="page"` from the same `isActive`, so styling
     * the item without correcting the attribute would have left the bar looking
     * right and announcing wrong — the worse of the two failures.
     */
    function isCurrent(item: NavItem): boolean {
        const paths = [item.to, ...(item.match ?? [])];

        return paths.some((path) =>
            item.end === true
                ? pathname === path
                : pathname === path || pathname.startsWith(`${path}/`),
        );
    }

    return (
        <div className={styles.shell}>
            {/* First tab stop on every page, per WCAG 2.4.1. */}
            <a className="visually-hidden" href="#hoofdinhoud">
                Naar de hoofdinhoud
            </a>

            <div className={styles.frame}>
                <header className={styles.header}>
                    <Link
                        to={routes.dashboard}
                        className={styles.brand}
                        aria-label="ThuisCoach, naar het overzicht"
                    >
                        <Logo size="sm" />
                    </Link>

                    <nav className={styles.nav} aria-label="Hoofdnavigatie">
                        {NAV_ITEMS.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`${styles.navLink} ${isCurrent(item) ? styles.navLinkActive : ''}`}
                                aria-current={isCurrent(item) ? 'page' : undefined}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    <div className={styles.headerActions}>
                        <ThemeToggle />
                        <ProfileMenu />
                    </div>
                </header>

                <main className={styles.main} id="hoofdinhoud">
                    <Outlet />
                </main>
            </div>

            <nav className={styles.bottomNav} aria-label="Hoofdnavigatie">
                {/*
                    The profile is a bar item here and an avatar on desktop. On a
                    phone it holds progress, history and measurements, and a
                    32px circle in the top corner is the wrong size of target
                    for something that leads to half the app.
                */}
                {[...NAV_ITEMS, PROFILE_ITEM].map((item) => (
                    <Link
                        key={item.to}
                        to={item.to}
                        className={`${styles.bottomLink} ${isCurrent(item) ? styles.bottomLinkActive : ''}`}
                        aria-current={isCurrent(item) ? 'page' : undefined}
                        /*
                         * Named in full even though the visible label is
                         * shortened for the bar. "Oefening" is the width that
                         * fits; "Oefeningen" is what the destination is called
                         * everywhere else, and a screen reader announcing a
                         * different name than the desktop bar makes the two
                         * layouts feel like two apps.
                         */
                        aria-label={item.label}
                    >
                        <svg
                            className={styles.bottomIcon}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            {item.icon}
                        </svg>
                        {item.shortLabel}
                    </Link>
                ))}
            </nav>
        </div>
    );
}
